import React from 'react';
import { motion } from 'framer-motion';
function OrganicShape({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 200 200" className={className} aria-hidden="true" data-id="element-173">
      <path d="M100 20C140 20 170 50 175 90C180 130 160 160 120 175C80 190 40 170 25 130C10 90 30 50 70 30C85 22 95 20 100 20Z" fill="currentColor" data-id="element-174" />
    </svg>;
}
export function Philosophy() {
  return <section id="philosophy" className="bg-cream py-24 md:py-32 relative overflow-hidden" data-id="element-175">
      {/* Background organic shapes */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-176">
        <OrganicShape className="absolute -top-20 -left-20 w-64 h-64 text-eucalyptus/10" data-id="element-177" />
        <OrganicShape className="absolute -bottom-32 -right-16 w-80 h-80 text-sage/8" data-id="element-178" />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-179">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-180">
          {/* Image/Illustration side */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="relative" data-id="element-181">
            <div className="aspect-[4/5] rounded-3xl bg-eucalyptus/20 overflow-hidden relative" data-id="element-182">
              {/* Botanical illustration */}
              <svg viewBox="0 0 400 500" className="absolute inset-0 w-full h-full" aria-hidden="true" data-id="element-183">
                {/* Large central leaf */}
                <motion.g initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 1,
                delay: 0.3
              }} viewport={{
                once: true
              }} data-id="element-184">
                  <path d="M200 50C200 50 120 120 120 220C120 320 200 400 200 400C200 400 280 320 280 220C280 120 200 50 200 50Z" fill="#8B9D83" fillOpacity="0.2" data-id="element-185" />
                  <path d="M200 70V380" stroke="#8B9D83" strokeOpacity="0.4" strokeWidth="2" data-id="element-186" />
                  {/* Leaf veins */}
                  <path d="M200 120C170 140 150 170 145 200" stroke="#8B9D83" strokeOpacity="0.25" strokeWidth="1" data-id="element-187" />
                  <path d="M200 120C230 140 250 170 255 200" stroke="#8B9D83" strokeOpacity="0.25" strokeWidth="1" data-id="element-188" />
                  <path d="M200 180C165 200 145 235 140 270" stroke="#8B9D83" strokeOpacity="0.25" strokeWidth="1" data-id="element-189" />
                  <path d="M200 180C235 200 255 235 260 270" stroke="#8B9D83" strokeOpacity="0.25" strokeWidth="1" data-id="element-190" />
                  <path d="M200 250C170 270 155 300 152 330" stroke="#8B9D83" strokeOpacity="0.25" strokeWidth="1" data-id="element-191" />
                  <path d="M200 250C230 270 245 300 248 330" stroke="#8B9D83" strokeOpacity="0.25" strokeWidth="1" data-id="element-192" />
                </motion.g>

                {/* Smaller accent leaves */}
                <motion.g initial={{
                opacity: 0,
                scale: 0.8
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.8,
                delay: 0.5
              }} viewport={{
                once: true
              }} data-id="element-193">
                  <path d="M80 150C80 150 60 180 60 210C60 240 80 260 80 260C80 260 100 240 100 210C100 180 80 150 80 150Z" fill="#A8B5A0" fillOpacity="0.25" data-id="element-194" />
                  <path d="M80 155V255" stroke="#A8B5A0" strokeOpacity="0.3" strokeWidth="1" data-id="element-195" />
                </motion.g>

                <motion.g initial={{
                opacity: 0,
                scale: 0.8
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.8,
                delay: 0.7
              }} viewport={{
                once: true
              }} data-id="element-196">
                  <path d="M320 200C320 200 300 230 300 260C300 290 320 310 320 310C320 310 340 290 340 260C340 230 320 200 320 200Z" fill="#6B7A5E" fillOpacity="0.2" data-id="element-197" />
                  <path d="M320 205V305" stroke="#6B7A5E" strokeOpacity="0.3" strokeWidth="1" data-id="element-198" />
                </motion.g>

                {/* Small decorative elements */}
                <circle cx="60" cy="380" r="8" fill="#8B9D83" fillOpacity="0.15" data-id="element-199" />
                <circle cx="340" cy="420" r="6" fill="#A8B5A0" fillOpacity="0.2" data-id="element-200" />
                <circle cx="100" cy="450" r="5" fill="#6B7A5E" fillOpacity="0.15" data-id="element-201" />
              </svg>
            </div>

            {/* Floating accent */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} viewport={{
            once: true
          }} className="absolute -bottom-6 -right-6 bg-olive text-cream px-6 py-4 rounded-2xl shadow-botanical-lg" data-id="element-202">
              <p className="font-serif text-lg italic" data-id="element-203">Est. 2018</p>
            </motion.div>
          </motion.div>

          {/* Content side */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} data-id="element-204">
            <span className="inline-block font-sans text-sm font-medium text-olive uppercase tracking-wider mb-4" data-id="element-205">
              Our Philosophy
            </span>

            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium text-botanical-text mb-6 leading-tight" data-id="element-206">
              Rooted in Nature,
              <br data-id="element-207" />
              <span className="text-sage-dark" data-id="element-208">Guided by Care</span>
            </h2>

            <div className="space-y-5 font-sans text-botanical-muted leading-relaxed" data-id="element-209">
              <p data-id="element-210">
                We believe that true wellness emerges from the harmony between
                body, mind, and the natural world. Our practice is founded on
                the principle that healing is not merely the absence of
                discomfort, but a journey toward wholeness.
              </p>

              <p data-id="element-211">
                Each session is a sanctuary—a space where the wisdom of
                traditional bodywork meets modern understanding of the nervous
                system. We honor your body's innate intelligence, working with
                it rather than against it.
              </p>

              <p data-id="element-212">
                Drawing inspiration from the patience of growing things, we
                approach each treatment with presence and intention, creating
                the conditions for your natural vitality to flourish.
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} viewport={{
            once: true
          }} className="mt-10 flex flex-wrap gap-8" data-id="element-213">
              <div data-id="element-214">
                <p className="font-serif text-4xl font-medium text-olive" data-id="element-215">
                  500+
                </p>
                <p className="font-sans text-sm text-botanical-muted mt-1" data-id="element-216">
                  Clients Served
                </p>
              </div>
              <div data-id="element-217">
                <p className="font-serif text-4xl font-medium text-olive" data-id="element-218">6</p>
                <p className="font-sans text-sm text-botanical-muted mt-1" data-id="element-219">
                  Years Experience
                </p>
              </div>
              <div data-id="element-220">
                <p className="font-serif text-4xl font-medium text-olive" data-id="element-221">
                  100%
                </p>
                <p className="font-sans text-sm text-botanical-muted mt-1" data-id="element-222">
                  Natural Products
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}